import { LexicalEditor } from 'lexical';
import { RefObject, useMemo, useRef } from 'react';
import * as Constants from 'const';
import { BASE_PROPS_COMMAND, BaseProps } from '../Plugins/BasePropsPlugin';
import { LINK_COMMAND, LinkProps } from '../Plugins/LinkNodePlugin';
import { REFERENCES_FONT_COMMAND, ReferencesFontProps } from '../Plugins/ReferencesFontPlugin';
import { usePropsUpdater } from './usePropsUpdater';

export type LexicalOnChange = {
  baseProps: (values: BaseProps) => void;
  font: (values: ReferencesFontProps) => void;
  link: (values: LinkProps) => void;
  textContent: (value: string) => void;
};

type Props = {
  charStyle: Constants.CharStyle[];
  link: string | undefined;
  linkApplicable: boolean;
  scriptStyle?: Constants.ScriptType;
  hasTextContent: boolean;
};

type Setters = {
  charStyle: (style: Constants.CharStyle) => void; // will toggle one of inline styles
  link: (value: string | undefined) => void;
  scriptStyle: (style: Constants.ScriptType) => void;
  clearFormatting: () => void;
};

type LexicalReferencesEditorHook = {
  disableLink: boolean;
  fontStyles: string[];
  link: string;
  toggleLink: (link: string) => void;
  clearFormatting: () => void;
  toggleCharStyle: (style: Constants.CharStyle) => void;
  toggleScriptStyle: (style: Constants.ScriptType) => void;
  ref: RefObject<LexicalEditor>;
  onChange: LexicalOnChange;
  hasTextContent: boolean;
};

export function useLexicalReferencesEditor(): LexicalReferencesEditorHook {
  const ref = useRef<LexicalEditor>(null);

  const { props, updateProps } = usePropsUpdater<Props>({
    charStyle: [],
    link: undefined,
    linkApplicable: false,
    scriptStyle: undefined,
    hasTextContent: false,
  });

  const setters = useMemo((): Setters => ({
    charStyle: (style: Constants.CharStyle) => ref.current?.dispatchCommand(
      REFERENCES_FONT_COMMAND.CHAR_STYLE,
      style,
    ),
    link: (value: string | undefined) => ref.current?.dispatchCommand(LINK_COMMAND, value),
    scriptStyle: (value: Constants.ScriptType) => ref.current?.dispatchCommand(BASE_PROPS_COMMAND.SCRIPT_STYLE, value),
    clearFormatting: () => ref.current?.dispatchCommand(BASE_PROPS_COMMAND.CLEAR_FORMATTING, undefined),
  }), []);

  const onChange: LexicalOnChange = useMemo(() => ({
    baseProps: (values: BaseProps) => updateProps({
      scriptStyle: values.scriptStyle,
    }),
    font: ({ charStyle }) => updateProps({ charStyle }),
    link: values => updateProps({
      link: values.link,
      linkApplicable: values.linkApplicable,
    }),
    textContent: value => updateProps({
      hasTextContent: Boolean(value.length),
    }),
  }), [updateProps]);

  return {
    disableLink: !props.linkApplicable,
    ref,
    fontStyles: [...props.charStyle, props.scriptStyle].filter(Boolean) as string[],
    link: props.link ?? '',
    hasTextContent: props.hasTextContent,
    onChange,
    toggleLink: (link: string) => setters.link(link || undefined),
    clearFormatting: setters.clearFormatting,
    toggleCharStyle: setters.charStyle,
    toggleScriptStyle: setters.scriptStyle,
  };
}
